import { Component, inject, signal, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PaymentDetail, PaymentListItem, PaymentOperation, PaymentTimelineEvent } from '../../core/models';

// Status → PrimeNG tag severity. Semantic colour that reads at a glance in the list.
const STATUS_SEVERITY: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
    Captured: 'success',
    Authorized: 'info',
    Authorizing: 'info',
    Created: 'secondary',
    Failed: 'danger',
    Voided: 'warn',
    Refunded: 'warn',
    PartiallyRefunded: 'warn'
};

@Component({
    selector: 'app-payments',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule, SelectModule, SelectButtonModule, InputTextModule, TooltipModule, FormsModule, DecimalPipe, DatePipe],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Payments</span></ng-template>

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-selectbutton [options]="modeOptions" [(ngModel)]="mode" (onChange)="reload()" optionLabel="label" optionValue="value" [allowEmpty]="false" />
                <p-select [options]="merchantOptions()" [(ngModel)]="locationId" (onChange)="reload()" placeholder="All merchants" [showClear]="true" [filter]="true" styleClass="w-64" />
                <p-select [options]="statusOptions()" [(ngModel)]="status" (onChange)="reload()" placeholder="All statuses" [showClear]="true" styleClass="w-48" />
                <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search reference / order / card last4" class="w-72" />
                <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
                <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
            </div>

            <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                     [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Status</th><th>Amount</th><th>Merchant</th><th>Method</th>
                        <th>Card</th><th>Reference</th><th>Created</th><th></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-p>
                    <tr class="cursor-pointer" (click)="open(p)">
                        <td class="whitespace-nowrap"><p-tag [value]="p.status" [severity]="sev(p.status)" />
                            @if (p.isTest) { <p-tag value="TEST" severity="warn" styleClass="ml-1" /> }
                        </td>
                        <td class="font-medium tabular-nums">{{ p.currency }} {{ p.amount | number: '1.2-2' }}
                            @if (p.refundedAmount > 0) { <span class="text-surface-400 text-xs">(−{{ p.refundedAmount | number: '1.2-2' }})</span> }
                        </td>
                        <td>{{ p.merchantName }} <span class="text-surface-400 text-xs">({{ p.locationId }})</span></td>
                        <td>{{ p.methodCode }}</td>
                        <td>@if (p.cardLast4) { {{ p.cardBrand }} •••• {{ p.cardLast4 }} } @else { <span class="text-surface-400">—</span> }</td>
                        <td class="text-sm">{{ p.clientReference }}@if (p.clientOrderReference) { <span class="text-surface-400"> · {{ p.clientOrderReference }}</span> }</td>
                        <td class="text-sm text-surface-500">{{ p.createdAt | date: 'short' }}</td>
                        <td><p-button icon="pi pi-eye" [text]="true" size="small" (onClick)="open(p)" /></td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="8">{{ error() ?? 'No payments yet.' }}</td></tr>
                </ng-template>
            </p-table>
        </p-card>

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '52rem' }" [dismissableMask]="true"
                  [header]="'Payment ' + (detail()?.intentId ?? '')">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detail(); as d) {
                <div class="flex items-center gap-2 mb-4">
                    <p-tag [value]="d.status" [severity]="sev(d.status)" />
                    @if (d.isTest) { <p-tag value="TEST" severity="warn" /> }
                    <span class="text-lg font-semibold tabular-nums">{{ d.currency }} {{ d.amount | number: '1.2-2' }}</span>
                    @if (d.cardLast4) { <span class="text-surface-500">· {{ d.cardBrand }} ending {{ d.cardLast4 }}</span> }
                </div>
                @if (d.failureMessage) {
                    <p-message severity="error" styleClass="mb-3">{{ d.failureMessage }}</p-message>
                }

                <div class="grid grid-cols-2 gap-2 mb-4 text-sm">
                    <div><b>Merchant:</b> {{ d.merchantName }} ({{ d.locationId }})</div>
                    <div><b>Method:</b> {{ d.methodCode }}</div>
                    <div><b>Client reference:</b> {{ d.clientReference }}</div>
                    <div><b>Order reference:</b> {{ d.clientOrderReference ?? '—' }}</div>
                    <div><b>Authorized:</b> {{ d.authorizedAmount | number: '1.2-2' }}</div>
                    <div><b>Captured:</b> {{ d.capturedAmount | number: '1.2-2' }}</div>
                    <div><b>Refunded:</b> {{ d.refundedAmount | number: '1.2-2' }}</div>
                    <div><b>Created:</b> {{ d.createdAt | date: 'medium' }}</div>
                    <div class="col-span-2"><b>Gateway order:</b> {{ d.gatewayOrderId ?? '—' }}
                        @if (d.authorizationId) { · <b>auth:</b> {{ d.authorizationId }} }
                        @if (d.authCode) { · <b>auth code:</b> {{ d.authCode }} }
                        @if (d.captureId) { · <b>capture:</b> {{ d.captureId }} }
                    </div>
                </div>

                <h6>Operation trail<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Each call made to the payment processor for this payment. Use View call to see what was sent and received." tooltipPosition="top"></i></h6>
                <p-table [value]="d.operations" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>Step</th><th>Result</th><th>Attempt</th><th>Gateway ref</th><th>Code</th><th>HTTP</th><th>When</th><th></th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-o let-i="rowIndex">
                        <tr>
                            <td>{{ o.type }}</td>
                            <td><p-tag [value]="o.status" [severity]="o.status === 'Succeeded' ? 'success' : (o.status === 'Failed' ? 'danger' : 'secondary')" /></td>
                            <td>{{ o.attemptNumber }}</td>
                            <td class="text-sm">{{ o.gatewayReference ?? '—' }}</td>
                            <td class="text-sm">{{ o.gatewayRespCode ?? (o.errorCategory ?? '—') }}</td>
                            <!-- The transport-level answer, beside the processor's own code. A 200 carrying a
                                 decline and a 500 carrying nothing are different incidents; "no reply" is a
                                 third, and it is the one a stored status code cannot express.
                                 The duration sits UNDER the status rather than beside it: scanning a trail for
                                 the slow step is a column-wise read, and "no reply" + a five-digit number on
                                 one line pushes every later column across. Absent on legacy rows, where the
                                 cell simply keeps its single line. -->
                            <td class="text-sm tabular-nums">
                                @if (o.responseStatusCode) {
                                    <span [class.text-red-500]="o.responseStatusCode >= 400">{{ o.responseStatusCode }}</span>
                                } @else if (o.requestUrl) {
                                    <span class="text-orange-500">no reply</span>
                                } @else {
                                    <span class="text-surface-400">—</span>
                                }
                                @if (durationLabel(o); as took) {
                                    <span class="block text-xs text-surface-500 dark:text-surface-400">{{ took }}</span>
                                }
                            </td>
                            <td class="text-sm text-surface-500">{{ o.at | date: 'medium' }}</td>
                            <td class="whitespace-nowrap">
                                <!-- A LABELLED control, not a bare glyph. The bodies were already here and
                                     reachable behind a "<>" icon, and the operator who asked for this feature
                                     never found it — an affordance nobody recognises is the same as no
                                     affordance. -->
                                @if (o.requestUrl || o.rawRequest || o.rawResponse) {
                                    <p-button [label]="expandedOp() === i ? 'Hide call' : 'View call'"
                                              [icon]="expandedOp() === i ? 'pi pi-chevron-up' : 'pi pi-search'"
                                              [text]="true" size="small" (onClick)="toggleOp(i)" />
                                }
                            </td>
                        </tr>
                        <!-- THE CALL, laid out as a request log: where it went, what came back, then both
                             bodies. The two bodies are LABELLED because side by side and unlabelled they are
                             easy to mistake for one another mid-incident, which is the opposite of what this
                             panel is for. "Sent" is qualified too: it is an allowlisted projection, not the
                             bytes the processor received. -->
                        @if ((o.requestUrl || o.rawRequest || o.rawResponse) && expandedOp() === i) {
                            <tr>
                                <td colspan="8">
                                    <div class="flex flex-col gap-3">
                                        <div>
                                            <div class="text-xs font-semibold mb-1">Sent to</div>
                                            @if (o.requestUrl) {
                                                <div class="flex items-start gap-2">
                                                    <code class="text-xs font-mono bg-surface-100 dark:bg-surface-800 rounded px-2 py-1 break-all select-all flex-1">{{ callLine(o) }}</code>
                                                    <p-button icon="pi pi-copy" [text]="true" size="small" (onClick)="copy('url-' + i, callLine(o))" />
                                                    @if (copiedKey() === 'url-' + i) { <span class="text-xs text-surface-500 self-center">Copied</span> }
                                                </div>
                                            } @else {
                                                <p class="text-xs text-surface-500 dark:text-surface-400">No address recorded for this step.</p>
                                            }
                                            <!-- One sentence, composed in TS, because its four shapes differ by
                                                 more than a word: with/without a status crossed with
                                                 with/without a duration. Assembling that from nested @if in
                                                 the template is where the stray full stop and the double
                                                 space come from. -->
                                            <p class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                                {{ answerLine(o) }}
                                                Recorded {{ o.at | date: 'medium' }}.
                                            </p>
                                        </div>

                                        <!-- Said once, plainly, so nobody hunts for a fuller copy that does
                                             not exist: what is shown IS the stored record. -->

                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <div class="flex items-center gap-2 mb-1">
                                                    <span class="text-xs font-semibold">Request body<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Card details are never stored and long content is shortened. This is the fullest copy available." tooltipPosition="top"></i></span>
                                                    @if (o.rawRequest) {
                                                        <p-button icon="pi pi-copy" [text]="true" size="small" (onClick)="copy('req-' + i, pretty(o.rawRequest))" />
                                                        @if (copiedKey() === 'req-' + i) { <span class="text-xs text-surface-500">Copied</span> }
                                                    }
                                                </div>
                                                @if (o.rawRequest) {
                                                    <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">{{ pretty(o.rawRequest) }}</pre>
                                                } @else {
                                                    <p class="text-xs text-surface-500 dark:text-surface-400">{{ noBody }}</p>
                                                }
                                            </div>
                                            <div>
                                                <div class="flex items-center gap-2 mb-1">
                                                    <span class="text-xs font-semibold">Response body</span>
                                                    @if (o.rawResponse) {
                                                        <p-button icon="pi pi-copy" [text]="true" size="small" (onClick)="copy('res-' + i, pretty(o.rawResponse))" />
                                                        @if (copiedKey() === 'res-' + i) { <span class="text-xs text-surface-500">Copied</span> }
                                                    }
                                                </div>
                                                @if (o.rawResponse) {
                                                    <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">{{ pretty(o.rawResponse) }}</pre>
                                                } @else {
                                                    <p class="text-xs text-surface-500 dark:text-surface-400">{{ noBody }}</p>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="8">No operations recorded.</td></tr></ng-template>
                </p-table>

                <h6 class="mt-4">Event timeline<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Everything recorded on this payment, in order." tooltipPosition="top"></i></h6>
                @if (timelineLoading()) {
                    <p class="text-sm text-surface-500 dark:text-surface-400">Loading timeline…</p>
                } @else {
                    <p-table [value]="timeline()" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th class="w-12">#</th><th>Event</th><th>When</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-ev let-i="rowIndex">
                            <tr>
                                <td class="text-surface-500">{{ ev.version }}</td>
                                <td><p-tag [value]="eventLabel(ev.eventType)" [severity]="eventSeverity(ev.eventType)" /></td>
                                <td class="text-sm text-surface-500">{{ ev.at | date: 'medium' }}</td>
                                <td><p-button [icon]="expandedEvent() === i ? 'pi pi-chevron-up' : 'pi pi-code'" [text]="true" size="small" (onClick)="toggleEvent(i)" /></td>
                            </tr>
                            @if (expandedEvent() === i) {
                                <tr>
                                    <td colspan="4">
                                        <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">{{ prettyData(ev) }}</pre>
                                    </td>
                                </tr>
                            }
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="4">No events recorded.</td></tr></ng-template>
                    </p-table>
                }
            }
        </p-dialog>
    `
})
export class Payments implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);

    readonly items = signal<PaymentListItem[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly statusOptions = signal<{ label: string; value: string }[]>([]);
    readonly pageSize = 25;

    search = '';
    status: string | null = null;
    locationId: string | null = null;
    mode = 'all';   // all | live | test
    readonly modeOptions = [
        { label: 'All', value: 'all' },
        { label: 'Live', value: 'live' },
        { label: 'Test', value: 'test' }
    ];
    readonly merchantOptions = signal<{ label: string; value: string }[]>([]);
    readonly expandedOp = signal<number | null>(null);

    readonly showDetail = signal(false);
    readonly detail = signal<PaymentDetail | null>(null);
    readonly detailLoading = signal(false);

    ngOnInit(): void {
        // Deep-link support: /payments?locationId=X pre-filters to that merchant (used by the
        // "View payments" button on the merchant management page).
        this.locationId = this.route.snapshot.queryParamMap.get('locationId');
        this.api.getPaymentStatuses().subscribe({
            next: (s) => this.statusOptions.set((s ?? []).map((v) => ({ label: v, value: v }))),
            error: () => {}
        });
        // Merchant filter = the onboarded locations (+ the dev merchant, which isn't Magento-onboarded).
        this.api.getLocations().subscribe({
            next: (r) => {
                const opts = (r.locations ?? []).map((l) => ({ label: (l.name ?? l.locationId) + ' (' + l.locationId + ')', value: l.locationId }));
                opts.push({ label: 'Dev Merchant (dev)', value: 'dev' });
                this.merchantOptions.set(opts);
            },
            error: () => this.merchantOptions.set([{ label: 'Dev Merchant (dev)', value: 'dev' }])
        });
        // NOTE: no load() here — the [lazy] p-table fires onLazyLoad -> onPage on first render, which
        // owns the initial fetch. Calling load(1) too would double the first request.
    }

    toggleOp(i: number): void {
        this.expandedOp.set(this.expandedOp() === i ? null : i);
        this.copiedKey.set(null);
    }

    pretty(raw: string): string {
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
    }

    /** "POST https://gateway.example/createOrder" — the method comes from what the ADAPTER recorded, never
     *  from a literal here, so a gateway that stops using POST does not make this screen lie. */
    callLine(o: PaymentOperation): string {
        return [o.requestMethod, o.requestUrl].filter((part) => !!part).join(' ');
    }

    /** "1,240 ms", or null when this step carries no measurement.
     *
     *  THE NULL CHECK IS EXPLICIT AND MUST STAY THAT WAY. `o.durationMs` is legitimately 0 for a call the
     *  processor answered in under a millisecond, so a truthiness test would hide a real measurement; and
     *  the inverse — rendering a missing duration as "0 ms" — would assert an instant call on every step
     *  recorded before the platform measured them. Absent and instant are different facts. Callers render
     *  null as nothing (the trail cell keeps its single line) rather than as a number. */
    durationLabel(o: PaymentOperation): string | null {
        const ms = o.durationMs;
        return ms === null || ms === undefined ? null : `${ms.toLocaleString()} ms`;
    }

    /** What came back, and how long it took, as ONE sentence — the panel's answer to "was this slow, and did
     *  it time out?".
     *
     *  Four shapes, because a status and a duration are independently present:
     *  "Answered HTTP 200 in 1,240 ms." · "Answered HTTP 500." (a legacy row) ·
     *  "No reply after 30,012 ms — …" (the timeout this field exists for) · "No HTTP response — …".
     *  The no-reply wording says AFTER, not IN: nothing arrived, so the number is how long we waited rather
     *  than how long an exchange took. Empty when nothing was sent — the endpoint block above already
     *  explains that case and a second sentence about it would just restate it. */
    answerLine(o: PaymentOperation): string {
        const took = this.durationLabel(o);

        if (o.responseStatusCode) {
            return took ? `Answered HTTP ${o.responseStatusCode} in ${took}.` : `Answered HTTP ${o.responseStatusCode}.`;
        }

        if (!o.requestUrl) return '';

        const lead = took ? `No reply after ${took}` : 'No HTTP response';
        return `${lead} — the request was sent and nothing came back.`;
    }

    /** Why a body is missing. Deliberately covers BOTH reasons in one sentence rather than working out
     *  which applies: the API returns null for "nothing stored" and for "not entitled" alike, and an
     *  "exists but withheld" marker would be a disclosure oracle. */
    readonly noBody = 'Not stored for this step, or your role cannot view it (payments:manage).';

    /** Which copy button last succeeded, so the confirmation is inline and scoped to that button. No toast:
     *  this page reports inline (p-message / text) only. */
    readonly copiedKey = signal<string | null>(null);

    copy(key: string, value: string): void {
        navigator.clipboard?.writeText(value).then(
            () => this.copiedKey.set(key),
            // A clipboard the browser refuses is not worth an error banner — the text is selectable.
            () => this.copiedKey.set(null)
        );
    }

    sev(status: string) {
        return STATUS_SEVERITY[status] ?? 'secondary';
    }

    onPage(e: { first?: number | null; rows?: number | null }): void {
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        this.loading.set(true);
        this.error.set(null);
        this.api.getPayments({ page, pageSize: this.pageSize, search: this.search || undefined, status: this.status || undefined, locationId: this.locationId || undefined, mode: this.mode !== 'all' ? this.mode : undefined }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                this.total.set(r.total ?? 0);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    readonly timeline = signal<PaymentTimelineEvent[]>([]);
    readonly timelineLoading = signal(false);
    readonly expandedEvent = signal<number | null>(null);

    open(p: PaymentListItem): void {
        this.showDetail.set(true);
        this.expandedOp.set(null);
        this.expandedEvent.set(null);
        this.detail.set(null);
        this.timeline.set([]);
        this.detailLoading.set(true);
        this.timelineLoading.set(true);
        this.api.getPaymentDetail(p.intentId).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: () => { this.detailLoading.set(false); this.showDetail.set(false); }
        });
        this.api.getPaymentTimeline(p.intentId).subscribe({
            next: (t) => { this.timeline.set(t); this.timelineLoading.set(false); },
            // The timeline is additive: its failure must not take down the detail view.
            error: () => { this.timelineLoading.set(false); }
        });
    }

    toggleEvent(i: number): void {
        this.expandedEvent.set(this.expandedEvent() === i ? null : i);
    }

    /** Stored alias -> human label: 'payment_captured' -> 'payment captured'. */
    eventLabel(type: string): string {
        return (type || '').replace(/^payments?_/, '').replace(/_v\d+$/, '').replaceAll('_', ' ');
    }

    eventSeverity(type: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const t = (type || '');
        if (t.includes('captured') || t.includes('authorized') || t.includes('order_committed')) return 'success';
        if (t.includes('declined') || t.includes('unknown')) return 'danger';
        if (t.includes('refunded') || t.includes('voided') || t.includes('expired')) return 'warn';
        if (t.includes('claimed') || t.includes('card_recorded') || t.includes('gateway_order')) return 'info';
        return 'secondary';
    }

    prettyData(ev: PaymentTimelineEvent): string {
        try { return JSON.stringify(ev.data, null, 2); } catch { return String(ev.data); }
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the server.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
